import { TypedDocumentNode, gql } from '@apollo/client';
import { generatePath } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { MLB_MY_CLUB_SLUG_COLLECTIONS_COLLECTIONSLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useEventsContext } from '@sorare/core/src/contexts/events';

import { ProgressBar } from 'components/collections/CollectionEvent/ProgressBar';
import { TeamLogoWithSeasonOverlay } from 'components/collections/CollectionEvent/TeamLogoWithSeasonOverlay';

import { CollectionEventProgress_cardCollection } from './__generated__/index.graphql';

export const ALL_STAR_THRESHOLDS = {
  [Rarity.limited]: 150,
  [Rarity.rare]: 150,
  [Rarity.super_rare]: 75,
  [Rarity.unique]: 75,
} as Record<
  Rarity.limited | Rarity.rare | Rarity.super_rare | Rarity.unique,
  number
>;

export const POSTSEASON_THRESHOLDS = {
  [Rarity.limited]: 180,
  [Rarity.rare]: 180,
  [Rarity.super_rare]: 100,
  [Rarity.unique]: 75,
} as Record<
  Rarity.limited | Rarity.rare | Rarity.super_rare | Rarity.unique,
  number
>;

const TeamProgressBar = styled.div<{ pct: number }>`
  flex: 1;
  align-self: flex-start;

  &.limited {
    --c-rarity: var(--c-rgb-limited);
  }

  &.rare {
    --c-rarity: var(--c-rgb-rare);
  }

  &.super_rare {
    --c-rarity: 0, 153, 255; /* rgb color of --c-collection-superRare */
  }

  &.unique {
    --c-rarity: 221, 82, 255; /* rgb color of --c-collection-unique */
  }

  &.flat {
    --active-bg-color: var(--c-rarity);
    --animated-border-bottom: 2px solid var(--c-active-tab-border);
    --button-border-radius: 24px 24px 0 0;
  }

  ${props =>
    props.pct >= 1
      ? css`
          --progress-color: rgba(var(--c-rarity), 1);
        `
      : css`
          --progress-color: rgba(var(--c-rarity), ${0.6 * props.pct});
          .isCollectionLink:hover & {
            --progress-color: rgba(var(--c-rgb-brand), 1);
          }
        `}
`;

type Props = {
  userSlug: string;
  collection: CollectionEventProgress_cardCollection;
  thresholds: typeof ALL_STAR_THRESHOLDS | typeof POSTSEASON_THRESHOLDS;
};

export const CollectionEventProgress = ({
  userSlug,
  collection,
  thresholds,
}: Props) => {
  const { track } = useEventsContext();

  const collectionRarity = collection.rarity! as
    | 'limited'
    | 'rare'
    | 'super_rare'
    | 'unique';
  if (!collection.userCardCollection || !thresholds[collectionRarity]) {
    return null;
  }
  const pct =
    Math.round(
      (collection.userCardCollection.score / thresholds[collectionRarity]) * 100
    ) / 100;
  const boost = Math.pow(1 - pct, 2);
  const pctBoosted = pct * (1 + boost);
  const valueBoost = Math.round(thresholds[collectionRarity] * pctBoosted);

  return (
    <Horizontal
      as={Link}
      to={generatePath(MLB_MY_CLUB_SLUG_COLLECTIONS_COLLECTIONSLUG, {
        slug: userSlug,
        collectionSlug: collection.slug,
      })}
      onClick={() =>
        track('Clicking Collection Event CollectionItem', {
          sport: Sport.BASEBALL,
          filledSlots: collection.userCardCollection?.fulfilledSlotsCount ?? 0,
          rarity: collectionRarity,
          score: collection.userCardCollection?.score ?? 0,
          passThreshold: pct >= 1,
          season: collection.season!.startYear,
        })
      }
      className="isCollectionLink"
    >
      <TeamLogoWithSeasonOverlay
        team={collection.team!}
        season={collection.season!.startYear}
      />

      <TeamProgressBar className={collectionRarity} pct={pctBoosted}>
        <ProgressBar
          value={valueBoost}
          displayedValue={collection.userCardCollection.score}
          min={0}
          max={thresholds[collectionRarity]}
        />
      </TeamProgressBar>
    </Horizontal>
  );
};

CollectionEventProgress.fragments = {
  cardCollection: gql`
    fragment CollectionEventProgress_cardCollection on CardCollection {
      slug
      userCardCollection(forUserSlug: $userSlug) {
        id
        score
        fulfilledSlotsCount
      }
      season {
        id
        name
        startYear
      }
      rarity
      team: anyTeam {
        slug
        ...TeamLogoWithSeasonOverlay_team
      }
    }
    ${TeamLogoWithSeasonOverlay.fragments.team}
  ` as TypedDocumentNode<CollectionEventProgress_cardCollection>,
};
